import React, { useState, useCallback } from 'react';
import styles from './styles.module.scss';
import { SlideInput } from '../../types';
import { isValidPhone, isValidEmail, isValidZipCode, hasAllSameDigits } from '../../utils/helpers';
import type { OptionalConsentAnswers } from '../../../shared/consent/OptionalConsents';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

interface PhoneConsentsProps {
  answers: OptionalConsentAnswers;
  phoneValid: boolean;
  onChange: (answers: OptionalConsentAnswers) => void;
  onContinue: (answers: OptionalConsentAnswers) => void;
}

const PhoneConsents: React.FC<PhoneConsentsProps> = ({
  answers,
  phoneValid,
  onChange,
  onContinue,
}) => {
  const partnerChecked = answers.thirdPartyMarketing === true;

  const togglePartner = () => {
    onChange({ ...answers, thirdPartyMarketing: !partnerChecked });
  };

  return (
    <div className={styles.phoneConsents}>
      <p className={styles.consentHint}>Facoltativi, revocabili in ogni momento</p>

      <label className={styles.partnerCard}>
        <span className={styles.partnerCardTop}>
          <span className={styles.partnerTitle}>Comunicazione dei dati ai partner</span>
          <input
            type="checkbox"
            className={`${styles.consentCheckbox} ${
              phoneValid && !partnerChecked ? styles.consentCheckboxReady : ''
            }`}
            checked={partnerChecked}
            onChange={togglePartner}
          />
        </span>
        <span className={styles.partnerDescription}>
          Acconsento a comunicare i miei dati ai partner in{' '}
          <a
            className={styles.privacyLink}
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            elenco
          </a>
          , per ricevere le loro offerte. Agiscono come titolari autonomi.
        </span>
      </label>

      <button
        type="button"
        className={`${styles.discoverCta} ${phoneValid ? styles.discoverCtaReady : ''}`}
        onClick={() => onContinue(answers)}
      >
        Scopri il tuo risparmio
      </button>

      <p className={styles.callNote}>
        Ti chiameremo per il preventivo; la chiamata può essere registrata.{' '}
        <a
          className={styles.privacyLink}
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Informativa privacy
        </a>
      </p>
    </div>
  );
};

interface FormInputProps {
  inputs: SlideInput[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
  isPhoneSlide?: boolean;
  optionalConsents?: OptionalConsentAnswers;
  onOptionalConsentsChange?: (answers: OptionalConsentAnswers) => void;
  onPhoneContinue?: (answers: OptionalConsentAnswers) => void;
}

/**
 * Componente form con input fields
 */
const FormInput: React.FC<FormInputProps> = ({
  inputs,
  values,
  onChange,
  onSubmit,
  submitLabel = 'Avanti',
  showBackButton = true,
  onBack,
  backLabel = 'Indietro',
  isPhoneSlide = false,
  optionalConsents,
  onOptionalConsentsChange,
  onPhoneContinue,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validate a single input
  const validateInput = useCallback((input: SlideInput, value: string): string => {
    if (input.required && !value.trim()) {
      return 'Campo obbligatorio';
    }

    switch (input.name) {
      case 'phone':
        if (value && !isValidPhone(value)) {
          if (hasAllSameDigits(value)) {
            return 'Il numero non può avere tutte le cifre uguali.';
          }
          return 'Numero di telefono non valido';
        }
        break;
      case 'email':
        if (value && !isValidEmail(value)) {
          return 'Indirizzo email non valido';
        }
        break;
      case 'zip':
        if (value && !isValidZipCode(value)) {
          return 'CAP non valido (5 cifre)';
        }
        break;
    }

    // Custom pattern validation
    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      if (!regex.test(value)) {
        return 'Formato non valido';
      }
    }

    return '';
  }, []);

  // Handle input change
  const handleChange = (input: SlideInput, value: string) => {
    onChange(input.name, value);

    // Validate on change if already touched
    if (touched[input.name]) {
      const error = validateInput(input, value);
      setErrors((prev) => ({ ...prev, [input.name]: error }));
    }
  };

  // Handle input blur
  const handleBlur = (input: SlideInput) => {
    setTouched((prev) => ({ ...prev, [input.name]: true }));
    const error = validateInput(input, values[input.name] || '');
    setErrors((prev) => ({ ...prev, [input.name]: error }));
  };

  // Check if form is valid
  const isFormValid = useCallback(() => {
    return inputs.every((input) => {
      const value = values[input.name] || '';
      const error = validateInput(input, value);
      return !error;
    });
  }, [inputs, values, validateInput]);

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all inputs
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {})
    );

    if (isPhoneSlide && optionalConsents && onPhoneContinue) {
      submitPhone(optionalConsents);
      return;
    }

    if (!hasErrors && isFormValid()) {
      onSubmit();
    }
  };

  const submitPhone = (answers: OptionalConsentAnswers) => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {}));

    if (hasErrors || !isFormValid() || !onPhoneContinue) {
      return;
    }

    const submitted: OptionalConsentAnswers = {
      enpalMarketingConsent: false,
      thirdPartyMarketing: answers.thirdPartyMarketing === true,
      thirdPartyProfiling: false,
    };
    onOptionalConsentsChange?.(submitted);
    onPhoneContinue(submitted);
  };

  return (
    <form className={styles.formGroup} onSubmit={handleSubmit}>
      {inputs.map((input) => {
          // Determina il valore autocomplete corretto per ogni campo
          const getAutocomplete = (name: string): string => {
            switch (name) {
              case 'name': return 'name';
              case 'email': return 'email';
              case 'phone': return 'tel';
              case 'zip': return 'postal-code';
              default: return 'off';
            }
          };

          return (
        <div key={input.name} className={styles.inputWrapper}>
          <label className={styles.inputLabel}>{input.placeholder}</label>
          <input
            className={`${styles.input} ${
              touched[input.name] && errors[input.name] ? styles.inputError : ''
            } ${isPhoneSlide && input.name === 'phone' ? styles.phoneInput : ''}`}
            type={input.type}
            name={input.name}
            placeholder={input.placeholder}
            value={values[input.name] || ''}
            onChange={(e) => handleChange(input, e.target.value)}
            onBlur={() => handleBlur(input)}
            required={input.required}
            autoComplete={getAutocomplete(input.name)}
          />
          {touched[input.name] && errors[input.name] && (
            <p className={styles.errorMessage}>{errors[input.name]}</p>
          )}
        </div>
          );
      })}

      {!isPhoneSlide && (
        <div className={styles.buttonGroup}>
          {showBackButton && onBack && (
            <button type="button" className={styles.btnPrev} onClick={onBack}>
              {backLabel}
            </button>
          )}
          <button
            type="submit"
            className={styles.btnNext}
            disabled={!isFormValid()}
          >
            {submitLabel}
          </button>
        </div>
      )}

      {isPhoneSlide && optionalConsents && onOptionalConsentsChange && onPhoneContinue && (
        <PhoneConsents
          answers={optionalConsents}
          phoneValid={isFormValid()}
          onChange={onOptionalConsentsChange}
          onContinue={submitPhone}
        />
      )}
    </form>
  );
};

export default FormInput;

